import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, FileText, FileJson, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AuditLogFilters } from '@/types/auditLog';

interface AuditLogExportProps {
  isOpen: boolean;
  onClose: () => void;
  filters: AuditLogFilters;
  onExport: (filters: AuditLogFilters, format: 'csv' | 'json') => Promise<void>;
  isExporting?: boolean;
}

export const AuditLogExport: React.FC<AuditLogExportProps> = ({
  isOpen,
  onClose,
  filters,
  onExport,
  isExporting = false,
}) => {
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [startDate, setStartDate] = useState(filters.startDate || '');
  const [endDate, setEndDate] = useState(filters.endDate || '');
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setError(null);

    // Validate date range
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    try {
      await onExport(
        {
          ...filters,
          startDate,
          endDate,
        },
        format
      );
      onClose();
    } catch (err) {
      setError('Failed to export audit logs. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Export Audit Logs
                  </h2>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Format Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Export Format
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setFormat('csv')}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
                        format === 'csv'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <FileText className={cn(
                        'w-5 h-5',
                        format === 'csv' ? 'text-blue-600' : 'text-gray-400'
                      )} />
                      <div className="text-left">
                        <div className={cn(
                          'text-sm font-medium',
                          format === 'csv' ? 'text-blue-700' : 'text-gray-700'
                        )}>
                          CSV
                        </div>
                        <div className="text-xs text-gray-500">
                          Spreadsheet format
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setFormat('json')}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
                        format === 'json'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <FileJson className={cn(
                        'w-5 h-5',
                        format === 'json' ? 'text-blue-600' : 'text-gray-400'
                      )} />
                      <div className="text-left">
                        <div className={cn(
                          'text-sm font-medium',
                          format === 'json' ? 'text-blue-700' : 'text-gray-700'
                        )}>
                          JSON
                        </div>
                        <div className="text-xs text-gray-500">
                          Developer format
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Date Range <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setError(null);
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-gray-400">to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setError(null);
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Current Filters Notice */}
                {(filters.userId || filters.entityType || filters.types?.length) && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <strong>Note:</strong> Your current filters will be applied to the export.
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
                <Button variant="outline" onClick={onClose} disabled={isExporting}>
                  Cancel
                </Button>
                <Button onClick={handleExport} disabled={isExporting}>
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
